import * as React from 'react';
import { Box, Icon } from '@leagueplatform/genesis-core';
import { Nullable, TreeNode, Tree } from '@web-config-app/core';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { EntityTreeItem } from './entity-tree-item/entity-tree-item.component';
import { ExpandIconSvg } from './entity-tree-item-icon.component';

/**
 * TODO: This file is for previewing tree calculation only. Actual UI will be
 * implemented in https://everlong.atlassian.net/browse/CACT-532
 */

export const renderTree = (
  tree: TreeNode[],
  isSidePanelExpanded: boolean,
  onAnchorClick?: (args: any) => void,
) =>
  Array.isArray(tree)
    ? tree.map((node, index) => (
        <EntityTreeItem
          key={node.id}
          path={node.id}
          itemId={node.id}
          label={node.name}
          type={node.type}
          index={index}
          onClick={onAnchorClick}
          isSidePanelExpanded={isSidePanelExpanded}
        >
          {Array.isArray(node.children)
            ? renderTree(node.children, isSidePanelExpanded, onAnchorClick)
            : null}
        </EntityTreeItem>
      ))
    : null;

export const CollapseIcon = () => (
  <Icon tint="$onSurfaceIconPrimary" icon={<ExpandIconSvg />} />
);
export const ExpandIcon = () => (
  <Icon
    tint="$onSurfaceIconPrimary"
    icon={<ExpandIconSvg />}
    css={{ transform: 'rotate(-90deg)' }}
  />
);

export interface EntityTreeViewProps {
  entityTree: Tree;
  expandedItems?: string[];
  onSelect?: (nodeId: string) => void;
  activeTreeNodeId?: Nullable<string>;
  onItemSelectionToggle?: (evt: React.SyntheticEvent, itemId: string) => void;
  defaultExpandedItems?: string[];
  preventNavigation?: boolean;
  isSidePanelExpanded?: boolean;
}

export const EntityTreeView: React.FC<EntityTreeViewProps> = ({
  entityTree,
  expandedItems,
  onSelect,
  preventNavigation,
  activeTreeNodeId,
  defaultExpandedItems,
  onItemSelectionToggle,
  isSidePanelExpanded = false,
}) => {
  const [firstItem] = entityTree;
  const selectedItem = activeTreeNodeId ?? firstItem?.id;

  return (
    <Box
      as="nav"
      css={{
        flex: '1 1 0',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'auto',
        '.MuiSimpleTreeView-root': {
          position: 'relative',
          width: '100%',
          '.MuiTreeItem-iconContainer': {
            width: '$oneAndHalf',
            height: '$oneAndHalf',
            position: 'relative',
          },
          '.MuiTreeItem-content': {
            // The row's border-box absorbs the 100vw padding-start, so
            // its width needs to include that buffer to actually reach
            // the panel's right edge after the -100vw margin shift.
            // When expanded, cap the width so labels wrap to fit the
            // panel; when collapsed, allow max-content so a nowrap label
            // can keep its background while the user scrolls.
            width: isSidePanelExpanded ? 'calc(100% + 100vw)' : 'max-content',
            minWidth: 'calc(100% + 100vw)',
            paddingInlineStart: 'calc($space$half + 100vw)',
            paddingInlineEnd: '$half',
            paddingBlock: '$half',
            marginInlineStart: '-100vw',
            borderRadius: '$none',
          },
          '.MuiTreeItem-content.Mui-selected, .MuiTreeItem-content.Mui-selected:hover':
            {
              backgroundColor: '$surfaceBackgroundTertiary',
            },
          '.MuiTreeItem-content:hover': {
            backgroundColor: 'transparent',
          },
          ul: {
            // this powers the array item counters
            counterReset: 'array-item-count',
          },
        },
      }}
    >
      <SimpleTreeView
        slots={{
          collapseIcon: CollapseIcon,
          expandIcon: ExpandIcon,
        }}
        expandedItems={expandedItems}
        defaultExpandedItems={defaultExpandedItems}
        selectedItems={selectedItem}
        onItemFocus={(evt: React.SyntheticEvent | null, itemId: string) => {
          if (preventNavigation) {
            evt?.stopPropagation();
            evt?.preventDefault();
          }
          if (typeof itemId === 'string') {
            onSelect?.(itemId);
          }
        }}
        onItemSelectionToggle={onItemSelectionToggle}
      >
        {renderTree(entityTree, isSidePanelExpanded, onSelect)}
      </SimpleTreeView>
    </Box>
  );
};
